
	import { defineComponent } from 'vue';

	import LoadingSpinner from '@/ChildrenComponents/LoadingSpinner.vue';
	import IncorrectPermissions from '@/ChildrenComponents/IncorrectPermissions.vue';
    import ReportExporter from '@/ChildrenComponents/ReportExporter.vue'

	import { ComponentType } from '@/Enums/ComponentType';

	import { IInternalUser } from '@/Models/IInternalUser';
	import { IRetentionRateReport, IFiscalYearReportSearch } from '@/Models/Report/IPhaseReport';
	import { IDataTableHeader } from '@/Models/IDataTableHeader';
    import { ITreatmentProgram } from '@/Models/ITreatmentProgram';

    import { ReportService } from '@/Services/Report/report-service';
    import { TreatmentProgramService } from '@/Services/treatment-program-service';
    import { doesUserRoleHaveComponentViewPermissions } from '@/Services/Helper/component-permissions-helper';
    import { isUserAdmin } from '@/Services/Helper/component-permissions-helper';
	import { useUserStore } from '@/Services/Store/user-store';

	class RetentionRateReport {
		source: IRetentionRateReport;

		constructor(src: IRetentionRateReport) {
			this.source = src;
		}

		private formatValue(value: number): number | string {
			// 0 int, 1 percent
			switch (this.source.dataType) {
				case 1: // percent
					return value.toFixed(2) + '%';
				default:
					return value;
			}
		}

		public get category(): string { return this.source.category; }
		public get description(): string { return this.source.description; }
		public get jul(): number | string { return this.formatValue(this.source.jul); }
		public get aug(): number | string { return this.formatValue(this.source.aug); }
		public get sep(): number | string { return this.formatValue(this.source.sep); }
		public get oct(): number | string { return this.formatValue(this.source.oct); }
		public get nov(): number | string { return this.formatValue(this.source.nov); }
		public get dec(): number | string { return this.formatValue(this.source.dec); }
		public get jan(): number | string { return this.formatValue(this.source.jan); }
		public get feb(): number | string { return this.formatValue(this.source.feb); }
		public get mar(): number | string { return this.formatValue(this.source.mar); }
		public get apr(): number | string { return this.formatValue(this.source.apr); }
		public get may(): number | string { return this.formatValue(this.source.may); }
		public get jun(): number | string { return this.formatValue(this.source.jun); }
	}

	export default defineComponent({
		name: 'retention-rate-report',
		components: { LoadingSpinner, IncorrectPermissions, ReportExporter },
		setup(): { currentUser: IInternalUser | null } {
			const userStoreInstance = useUserStore();
			return { currentUser: userStoreInstance.$state.cstInternalUser };
		},
		created() {
			this.loadLookups();
		},
		data () {
			return {
				reportData: [] as RetentionRateReport[],
				isLoading: false,
				isSearching: false,
				searchDto: { year: null, programIds: [], programId: null } as IFiscalYearReportSearch,
                allPrograms: [] as ITreatmentProgram[],
				headers: [
                    { title: "Category", key: "category", sortable: true, align: "start" },
                    { title: "Jul", key: "jul", sortable: true, align: "start", width: 25 },
                    { title: "Aug", key: "aug", sortable: true, align: "start", width: 25 },
                    { title: "Sep", key: "sep", sortable: true, align: "start", width: 25 },
                    { title: "Oct", key: "oct", sortable: true, align: "start", width: 25 },
                    { title: "Nov", key: "nov", sortable: true, align: "start", width: 25 },
                    { title: "Dec", key: "dec", sortable: true, align: "start", width: 25 },
                    { title: "Jan", key: "jan", sortable: true, align: "start", width: 25 },
                    { title: "Feb", key: "feb", sortable: true, align: "start", width: 25 },
                    { title: "Mar", key: "mar", sortable: true, align: "start", width: 25 },
                    { title: "Apr", key: "apr", sortable: true, align: "start", width: 25 },
                    { title: "May", key: "may", sortable: true, align: "start", width: 25 },
                    { title: "Jun", key: "jun", sortable: true, align: "start", width: 25 },
				] as IDataTableHeader[],
			}
		},
		methods: {
			loadLookups() {
				this.isLoading = true;
				TreatmentProgramService.getAllTreatmentPrograms().then(response => this.allPrograms = response).finally(() => this.isLoading = false);
			},
			getReportData() {
				this.isSearching = true;
				ReportService.getRetentionRateReportData(this.searchDto).then(results => this.reportData = results.map(x => new RetentionRateReport(x))).finally(() => this.isSearching = false);
			},
		},
		computed: {
			canView(): boolean {
				return doesUserRoleHaveComponentViewPermissions(this.currentUser?.attachedRoles ?? [], ComponentType.Report);
			},
            userAttachedPrograms() : ITreatmentProgram[] {
                return isUserAdmin(this.currentUser?.attachedRoles ?? []) ? this.allPrograms : this.currentUser?.attachedPrograms ?? [];
            },
			fiscalYears(): { year: number, text: string }[] {
				// fiscal year runs Jul-Jun
				const now = new Date();
				const currentFiscalYear = now.getMonth() < 6 ? now.getFullYear() : now.getFullYear() + 1;
				const years = [] as { year: number, text: string }[];

				for (let i = currentFiscalYear; i > currentFiscalYear - 10; i--)
					years.push({ year: i, text: `${i - 1}-${i}` });

				return years;
			},
		},
	});
